import { Link } from "react-router-dom";
import { useInView } from "../../../hooks/useInView";
import { events } from "../../../mocks/events";

export default function UpcomingEvents() {
  const upcoming = events.filter((e) => e.status === "upcoming");
  const { ref, inView } = useInView();

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className="bg-white py-16 md:py-24 relative overflow-hidden"
    >
      {/* Subtle dot grid */}
      <div className="absolute inset-0 bg-dot-grid-light pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className={`mb-12 anim-hidden ${inView ? "animate-fade-in-up" : ""}`}>
          <div className="inline-block border border-gray-300 text-gray-500 font-condensed font-semibold text-xs uppercase tracking-widest px-3 py-1 mb-4">
            Coming Up
          </div>
          <h2 className="font-condensed font-black text-gray-900 text-4xl md:text-5xl uppercase tracking-tight">
            Upcoming Events
          </h2>
          <div className={`mt-3 h-0.5 bg-orange-500 w-16 anim-hidden ${inView ? "line-grow delay-200" : ""}`} />
        </div>

        <div className="flex flex-col gap-8">
          {upcoming.map((event, index) => (
            <div
              key={event.id}
              className={`flex flex-col lg:flex-row gap-0 overflow-hidden border transition-all duration-300 hover:-translate-y-0.5 anim-hidden ${
                inView ? "animate-fade-in-up" : ""
              } ${index === 0 ? "border-orange-200 hover:border-orange-400" : "border-gray-200 hover:border-gray-400"}`}
              style={{ animationDelay: `${150 + index * 150}ms` }}
            >
              {/* Poster */}
              <div className="w-full lg:w-64 xl:w-80 flex-shrink-0 h-64 overflow-hidden bg-gray-100 relative group">
                <img
                  src={event.image}
                  alt={event.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                {index === 0 && (
                  <div className="absolute top-3 left-3 bg-orange-500 text-white font-condensed font-bold text-xs uppercase tracking-widest px-2 py-1">
                    Next Event
                  </div>
                )}
              </div>

              {/* Content */}
              <div className={`flex-1 p-6 md:p-8 flex flex-col justify-between gap-6 ${index === 0 ? "bg-orange-50/30" : "bg-white"}`}>
                <div>
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="font-condensed font-bold text-orange-500 text-sm uppercase tracking-widest">
                      {event.date}
                    </span>
                    <span className="text-gray-300">|</span>
                    <span className="text-gray-500 text-sm font-condensed">{event.time}</span>
                  </div>
                  <h3 className="font-condensed font-black text-gray-900 text-2xl md:text-3xl uppercase tracking-tight mb-1">
                    {event.title}
                  </h3>
                  <p className="text-orange-500 font-condensed font-semibold text-sm uppercase tracking-wide mb-4">
                    {event.subtitle}
                  </p>
                  <p className="text-gray-500 text-sm leading-relaxed mb-5">
                    {event.description}
                  </p>

                  {/* Highlights */}
                  <div className="flex flex-wrap gap-2">
                    {event.highlights.map((h, i) => (
                      <span
                        key={i}
                        className="border border-gray-200 text-gray-600 font-condensed font-semibold text-xs uppercase tracking-wide px-2 py-1 hover:border-orange-300 hover:text-orange-600 transition-colors duration-200"
                      >
                        {h}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <Link
                    to="/events"
                    className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-condensed font-bold text-sm uppercase tracking-widest px-6 py-3 transition-all duration-200 cursor-pointer whitespace-nowrap group"
                  >
                    Get Tickets
                    <i className="ri-arrow-right-line transition-transform duration-200 group-hover:translate-x-1" />
                  </Link>
                  <div className="flex items-center gap-2 text-gray-500">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-map-pin-line text-orange-400 text-sm" />
                    </div>
                    <span className="text-xs font-condensed uppercase tracking-wide whitespace-nowrap">
                      {event.venue} — {event.city}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
